import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { useChat } from '../../../hooks/useChat';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const SEVERITY_CONFIG = {
  Positive: { color: '#10B981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)', icon: 'TrendingUp' },
  Warning: { color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', icon: 'AlertTriangle' },
  Critical: { color: '#EF4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)', icon: 'AlertCircle' },
};

const FALLBACK_INSIGHTS = [
  { title: 'Review Your Top Spending Categories', observation: 'Analyzing your spending patterns helps identify areas where you can save more effectively each month.', severity: 'Warning', recommendation: 'Set category-specific budget limits and review them weekly to stay on track.' },
  { title: 'Build Your Emergency Fund', observation: 'Having 3-6 months of expenses saved provides a financial safety net for unexpected events.', severity: 'Positive', recommendation: 'Automate a fixed monthly transfer to your emergency fund savings account.' },
  { title: 'Track Month-Over-Month Changes', observation: 'Monitoring spending trends helps you catch overspending before it becomes a problem.', severity: 'Warning', recommendation: 'Use the comparison table below to identify categories that increased this month.' },
];

export default function AIInsightCards({ spendingData, dateRange, totalBudget, totalSpent }) {
  const { user } = useAuth();
  const [insights, setInsights] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [hasGenerated, setHasGenerated] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (!user?.id) return;
    supabase?.from('user_profiles')?.select('income_range, risk_tolerance_score, spending_personality')?.eq('id', user?.id)?.single()?.then(({ data }) => { if (data) setUserProfile(data); });
  }, [user?.id]);

  const generateInsights = useCallback(() => {
    if (!spendingData?.length || isLoading) return;
    setDismissed(new Set());
    setHasGenerated(true);

    const categoryBreakdown = spendingData?.map(d => {
      const change = d?.lastMonth > 0 ? (((d?.thisMonth - d?.lastMonth) / d?.lastMonth) * 100)?.toFixed(1) : '0';
      return `${d?.category}: $${d?.thisMonth} this month (${change > 0 ? '+' : ''}${change}% vs last month)`;
    })?.join('\n');

    const budgetInfo = totalBudget ? `Total budget: $${totalBudget}, Total spent: $${totalSpent} (${((totalSpent / totalBudget) * 100)?.toFixed(0)}% used)` : '';
    const profileInfo = userProfile ? `Income range: ${userProfile?.income_range || 'not specified'}, Risk tolerance: ${userProfile?.risk_tolerance_score || 5}/10, Spending personality: ${userProfile?.spending_personality || 'balanced'}` : '';

    sendMessage([
      {
        role: 'system',
        content: `You are FinVisAR, an expert personal finance AI advisor. Analyze spending data and return ONLY a valid JSON array of 3-5 insight objects. Each object must have exactly these fields: "title" (max 8 words), "observation" (1-2 sentences with specific dollar amounts and percentages), "severity" (exactly one of: "Positive", "Warning", "Critical"), "recommendation" (1 actionable sentence). Be specific, data-driven, and personalized. Return ONLY the JSON array, no other text.`,
      },
      {
        role: 'user',
        content: `Analyze my ${dateRange} spending data and generate 3-5 personalized insights:\n\nSpending by category:\n${categoryBreakdown}\n\n${budgetInfo}\n${profileInfo}\n\nFocus on: overspending patterns, positive trends, budget risks, and actionable savings. Return ONLY a JSON array.`,
      },
    ], { max_completion_tokens: 1200 });
  }, [spendingData, isLoading, dateRange, totalBudget, totalSpent, userProfile, sendMessage]);

  useEffect(() => {
    if (response && hasGenerated && !isLoading) {
      try {
        const match = response?.match(/\[.*\]/s);
        if (match) {
          const parsed = JSON.parse(match?.[0]);
          if (Array.isArray(parsed) && parsed?.length > 0) {
            setInsights(parsed);
            return;
          }
        }
        setInsights(FALLBACK_INSIGHTS);
      } catch {
        setInsights(FALLBACK_INSIGHTS);
      }
    }
  }, [response, hasGenerated, isLoading]);

  useEffect(() => {
    if (error && hasGenerated) {
      setInsights(FALLBACK_INSIGHTS);
    }
  }, [error, hasGenerated]);

  const visibleInsights = insights?.filter((_, idx) => !dismissed?.has(idx));

  return (
    <div className="bg-[rgba(255,255,255,0.03)] border border-[rgba(0,229,255,0.15)] rounded-xl overflow-hidden">
      {/* Teal gradient header */}
      <div className="px-5 py-4" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.12) 0%, rgba(123,47,190,0.12) 100%)', borderBottom: '1px solid rgba(0,229,255,0.15)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.2), rgba(123,47,190,0.2))' }}>
              <Icon name="Sparkles" size={16} color="#00E5FF" />
            </div>
            <div>
              <h3 className="text-[#E2E8F0] font-semibold text-sm">AI Insights</h3>
              <p className="text-[#64748B] text-xs">Powered by GPT-5 · FinVisAR Advisor</p>
            </div>
          </div>
          <button
            onClick={generateInsights}
            disabled={isLoading || !spendingData?.length}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.15), rgba(123,47,190,0.15))', border: '1px solid rgba(0,229,255,0.25)', color: '#00E5FF' }}
          >
            {isLoading ? (
              <><div className="w-3 h-3 border border-[#00E5FF] border-t-transparent rounded-full animate-spin" />Analyzing...</>
            ) : (
              <><Icon name="Wand2" size={12} />{hasGenerated ? 'Regenerate' : 'Generate Insights'}</>
            )}
          </button>
        </div>
      </div>
      <div className="p-5">
        {/* Loading skeleton */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3]?.map(i => (
              <div key={i} className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)] animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-[rgba(255,255,255,0.06)] flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-[rgba(255,255,255,0.06)] rounded w-3/4" />
                    <div className="h-2.5 bg-[rgba(255,255,255,0.04)] rounded w-full" />
                    <div className="h-2.5 bg-[rgba(255,255,255,0.04)] rounded w-5/6" />
                  </div>
                </div>
              </div>
            ))}
            <p className="text-center text-xs text-[#64748B] mt-2">FinVisAR AI is analyzing your spending patterns...</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !hasGenerated && (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.1), rgba(123,47,190,0.1))' }}>
              <Icon name="Sparkles" size={24} color="#475569" />
            </div>
            <p className="text-[#94A3B8] text-sm font-medium mb-1">AI Spending Analysis</p>
            <p className="text-[#64748B] text-xs mb-4">Click "Generate Insights" to get personalized AI-powered spending analysis based on your data.</p>
            <button
              onClick={generateInsights}
              disabled={!spendingData?.length}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200 disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.15), rgba(123,47,190,0.15))', border: '1px solid rgba(0,229,255,0.25)', color: '#00E5FF' }}
            >
              <Icon name="Wand2" size={13} />
              Generate Insights
            </button>
          </div>
        )}

        {/* Insight cards */}
        {!isLoading && hasGenerated && visibleInsights?.length > 0 && (
          <div className="space-y-3">
            {insights?.map((insight, idx) => {
              if (dismissed?.has(idx)) return null;
              const cfg = SEVERITY_CONFIG?.[insight?.severity] || SEVERITY_CONFIG?.Warning;
              return (
                <div
                  key={idx}
                  className="p-4 rounded-xl border transition-all duration-200"
                  style={{ backgroundColor: cfg?.bg, borderColor: cfg?.border }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${cfg?.color}20` }}>
                      <Icon name={cfg?.icon} size={14} color={cfg?.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="text-[#E2E8F0] text-xs font-semibold leading-tight">{insight?.title}</p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{ color: cfg?.color, backgroundColor: `${cfg?.color}20` }}
                          >
                            {insight?.severity}
                          </span>
                          <button
                            onClick={() => setDismissed(prev => new Set([...prev, idx]))}
                            className="w-5 h-5 rounded flex items-center justify-center hover:bg-[rgba(255,255,255,0.08)] transition-colors text-[#475569] hover:text-[#94A3B8]"
                          >
                            <Icon name="X" size={11} />
                          </button>
                        </div>
                      </div>
                      <p className="text-[#94A3B8] text-xs leading-relaxed mb-2">{insight?.observation}</p>
                      <div className="flex items-start gap-1.5 p-2 rounded-lg bg-[rgba(255,255,255,0.04)]">
                        <Icon name="Lightbulb" size={11} color="#F59E0B" className="flex-shrink-0 mt-0.5" />
                        <p className="text-[#CBD5E1] text-[11px] leading-relaxed">{insight?.recommendation}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {dismissed?.size > 0 && dismissed?.size < insights?.length && (
              <p className="text-center text-xs text-[#475569]">{dismissed?.size} insight{dismissed?.size > 1 ? 's' : ''} dismissed</p>
            )}
            {dismissed?.size === insights?.length && (
              <div className="text-center py-4">
                <p className="text-[#64748B] text-xs mb-2">All insights dismissed</p>
                <button onClick={generateInsights} className="text-xs text-[#00E5FF] hover:underline">Regenerate</button>
              </div>
            )}
          </div>
        )}

        {/* Fallback error state */}
        {!isLoading && hasGenerated && error && insights?.length === 0 && (
          <div className="text-center py-6">
            <Icon name="AlertCircle" size={24} color="#EF4444" />
            <p className="text-[#EF4444] text-xs mt-2 mb-3">AI unavailable — showing default insights</p>
          </div>
        )}
      </div>
    </div>
  );
}
